import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const AdminLayout = ({ title, back = "/admin", backLabel = "Dashboard", children, testId }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = () => {
    api.post("/auth/logout").catch(() => {});
    logout();
    navigate("/admin/login", { replace: true });
  };
  return (
    <div className="min-h-screen bg-[#121214] pb-16" data-testid={testId}>
      <header className="sticky top-0 z-30 border-b border-zinc-800 bg-[#121214]/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-2 px-4 py-2">
          <Link to="/admin" className="font-display text-2xl text-[#FAF7F2]" data-testid="admin-home-link">
            JZ <span className="text-[#FFD23F]">Admin</span>
          </Link>
          <div className="flex items-center gap-1">
            <a href="/" target="_blank" rel="noreferrer" className="inline-flex h-11 items-center gap-1.5 rounded-md px-3 text-sm text-zinc-300 hover:bg-zinc-800" data-testid="admin-view-site-link">
              <ExternalLink className="h-4 w-4" /> <span className="hidden sm:inline">Live site</span>
            </a>
            <button onClick={doLogout} className="inline-flex h-11 items-center gap-1.5 rounded-md px-3 text-sm text-zinc-300 hover:bg-zinc-800" data-testid="admin-logout-button">
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 pt-5">
        {back && (
          <Link to={back} className="inline-flex h-11 items-center gap-1.5 text-sm text-zinc-400 hover:text-[#FFD23F]" data-testid="admin-back-link">
            <ArrowLeft className="h-4 w-4" /> {backLabel}
          </Link>
        )}
        {title && <h1 className="font-display mb-6 mt-1 text-4xl text-[#FAF7F2] sm:text-5xl" data-testid="admin-page-title">{title}</h1>}
        {children}
      </main>
    </div>
  );
};

export const Panel = ({ title, children, testId, className = "" }) => (
  <section className={`mb-6 rounded-lg border border-zinc-800 bg-[#1A1A1E] p-4 sm:p-6 ${className}`} data-testid={testId}>
    {title && <h2 className="font-sub mb-4 text-lg font-semibold uppercase tracking-wide text-[#FAF7F2]">{title}</h2>}
    {children}
  </section>
);

export const FieldError = ({ msg, testId }) =>
  msg ? <p className="mt-1.5 text-sm font-medium text-[#ff6b5e]" data-testid={testId} role="alert">{msg}</p> : null;

export const ConfirmDelete = ({ trigger, description, confirmLabel = "Yes, delete", onConfirm }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
    <AlertDialogContent className="w-[calc(100%-2rem)] max-w-md rounded-lg border-zinc-700 bg-[#1A1A1E] text-[#FAF7F2]" data-testid="confirm-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle className="font-display text-3xl">Are you sure?</AlertDialogTitle>
        {description && <AlertDialogDescription className="text-base text-zinc-300">{description}</AlertDialogDescription>}
      </AlertDialogHeader>
      <AlertDialogFooter className="gap-2">
        <AlertDialogCancel className="h-12 border-zinc-700 bg-transparent text-base" data-testid="confirm-cancel-button">Cancel</AlertDialogCancel>
        <AlertDialogAction className="h-12 bg-[#C8322B] text-base text-white hover:bg-[#a82620]" onClick={onConfirm} data-testid="confirm-yes-button">
          {confirmLabel}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const inputCls = "h-12 bg-[#121214] border-zinc-700 text-base";
export const btnPrimary = "h-12 px-6 text-base font-semibold bg-[#FFD23F] text-[#121214] hover:bg-[#e6b820]";
export const btnSecondary = "h-12 px-4 text-base border border-zinc-700 bg-transparent text-zinc-100 hover:bg-zinc-800";
